import { useCallback, useEffect, useState } from "react";
import { useToasts } from "../../contexts/toasts";
import { TimeUnderTension } from "../../types/Entities";
import { Entities } from "../../api/Entities";
import { Alert, Button, Container, Modal } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faExclamationTriangle, faPen, faPlus, faTrash } from "@fortawesome/free-solid-svg-icons";
import { TailSpin } from "react-loader-spinner";
import { ConfigProvider, Empty, Form, GetProp, Space, Table } from "antd";
import IconButton from "../../common/IconButton";
import { PagedResults } from "../../types/generics";
import TUTForm from "../../Components/Form/TUTForm";
import ListPageHeader from "../../common/ListPageHeader";
import ListPanel from "../../common/ListPanel";

export default function TUTsList() {
  const { showToast } = useToasts();
  const [tuts, setTuts] = useState<TimeUnderTension[]>();
  const [error, setError] = useState(false);
  const [totalTUTs, setTotalTUTs] = useState<number>();
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState<{ pageSize: number; currentPage: number }>({
    pageSize: 20,
    currentPage: 1,
  });
  const [addTUTModal, setAddTUTModal] = useState(false);
  const [deleteTUTModal, setDeleteTUTModal] = useState(false);
  const [tutForm] = Form.useForm();
  const [selectedTUT, setSelectedTUT] = useState<TimeUnderTension>();

  const initTUTs = useCallback(() => {
    const urlParams = new URLSearchParams();
    urlParams.append("page", `${filters.currentPage}`);
    urlParams.append("size", `${filters.pageSize}`);

    Entities.getPaginatedTUTs(urlParams).then(
      ({ data }) => {
        setTuts((data as PagedResults<TimeUnderTension>).items);
        setTotalTUTs((data as PagedResults<TimeUnderTension>).totalItems);
      },
      () => {
        showToast("tuts-error", "Impossibile ottenere la lista dei Time Under Tension", "", "danger");
        setTuts([]);
        setError(true);
      }
    );
  }, [filters, showToast]);

  const reset = useCallback(() => {
    setError(false);
    setTuts(undefined);
    initTUTs();
  }, [initTUTs]);

  useEffect(() => {
    reset();
  }, [filters, reset]);

  const handlePageChange = useCallback((page: number) => {
    setFilters((previous) => ({ ...previous, currentPage: page }));
  }, []);

  const handlePageSizeChange = useCallback((_current: number, size: number) => {
    setFilters((previous) => ({ ...previous, pageSize: size, currentPage: 1 }));
  }, []);

  const handleEditTUTClick = useCallback(
    (tut?: TimeUnderTension) => () => {
      setSelectedTUT(tut);
      setAddTUTModal(true);
    },
    []
  );

  const handleDeleteTUTClick = useCallback(
    (tut: TimeUnderTension) => () => {
      setSelectedTUT(tut);
      setDeleteTUTModal(true);
    },
    []
  );

  const renderToolbar = useCallback(() => {
    return (
      <ListPageHeader
        title="Schemi TUT"
        count={totalTUTs}
        actions={
          <button onClick={handleEditTUTClick()} className="button-primary">
            <FontAwesomeIcon icon={faPlus} />
            <span className="d-none d-sm-inline ms-2">Aggiungi Time Under Tension</span>
          </button>
        }
      />
    );
  }, [handleEditTUTClick, totalTUTs]);

  const renderEmpty: GetProp<typeof ConfigProvider, "renderEmpty"> = () => (
    <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Nessun TUT" />
  );

  const renderTUTs = useCallback(() => {
    if (error)
      return (
        <Alert variant="danger" className="py-2">
          <FontAwesomeIcon icon={faExclamationTriangle} />
          <span className="ms-2">C'è stato un errore, per favore riprova più tardi</span>
        </Alert>
      );

    return (
      <Table
        size="middle"
        bordered
        dataSource={tuts}
        rowKey="id"
        locale={{ emptyText: renderEmpty() }}
        pagination={{
          pageSize: filters.pageSize,
          current: filters.currentPage,
          onChange: handlePageChange,
          onShowSizeChange: handlePageSizeChange,
          total: totalTUTs,
        }}
      >
        <Table.Column
          key="tut"
          title="TUT"
          className="text-center col-pattern"
          render={(_value, record: TimeUnderTension) =>
            `${record.ecc}.${record.stopEcc}.${record.conc}.${record.stopConc}`
          }
        />
        <Table.Column
          key="actions"
          title="Azioni"
          className="text-center col-pattern"
          render={(_value, record: TimeUnderTension) => (
            <Space size={1}>
              <IconButton tooltip="Modifica" onClick={handleEditTUTClick(record)}>
                <FontAwesomeIcon icon={faPen} />
              </IconButton>
              <IconButton
                tooltip="Elimina"
                className="text-danger"
                onClick={handleDeleteTUTClick(record)}
              >
                <FontAwesomeIcon icon={faTrash} />
              </IconButton>
            </Space>
          )}
        />
      </Table>
    );
  }, [tuts, error, filters, handlePageChange, handlePageSizeChange, totalTUTs, handleEditTUTClick, handleDeleteTUTClick]);

  const renderAsyncContent = useCallback(() => {
    return <ListPanel loading={!tuts}>{renderTUTs()}</ListPanel>;
  }, [tuts, renderTUTs]);

  const handleAddTUTModalClose = useCallback(() => {
    setAddTUTModal(false);
    tutForm.resetFields();
    setSelectedTUT(undefined);
  }, [tutForm]);

  const handleDeleteTUTModalClose = useCallback(() => {
    setDeleteTUTModal(false);
    setSelectedTUT(undefined);
  }, []);

  const handleEditTUTSubmit = useCallback(
    (values: any) => {
      setLoading(true);

      const body = { ...values };
      const promise = selectedTUT
        ? Entities.updateTUT(selectedTUT.id!, body)
        : Entities.createTUT(body);

      promise.then(
        () => {
          setLoading(false);
          showToast(
            "tut-success",
            `Time Under Tension ${selectedTUT ? "Modificato" : "Creato"} correttamente`,
            undefined,
            "success"
          );
          handleAddTUTModalClose();
          reset();
        },
        () => {
          setLoading(false);
          showToast(
            "tut-error",
            `Errore nella ${selectedTUT ? "modifica" : "creazione"} del Time Under Tension`,
            undefined,
            "danger"
          );
        }
      );
    },
    [selectedTUT, showToast, handleAddTUTModalClose, reset]
  );

  const handleDeleteTUT = useCallback(() => {
    setLoading(true);
    Entities.deleteTUT(selectedTUT!.id!).then(
      () => {
        setLoading(false);
        handleDeleteTUTModalClose();
        reset();
        showToast("delete-tut-success", "Time Under Tension correttamente eliminato", undefined, "success");
      },
      () => {
        setLoading(false);
        showToast(
          "delete-tut-error",
          "Errore nella cancellazione del Time Under Tension, per favore riprova",
          undefined,
          "danger"
        );
      }
    );
  }, [selectedTUT, showToast, handleDeleteTUTModalClose, reset]);

  return (
    <Container fluid="md" className="mt-5 list-page-container">
      {renderToolbar()}
      {renderAsyncContent()}

      <Modal show={addTUTModal} onHide={handleAddTUTModalClose}>
        <Modal.Header closeButton>
          <Modal.Title>{selectedTUT ? "Modifica" : "Nuovo"} Time Under Tension</Modal.Title>
        </Modal.Header>
        <TUTForm
          tut={selectedTUT}
          onClose={handleAddTUTModalClose}
          onSubmit={handleEditTUTSubmit}
          loading={loading}
        />
      </Modal>

      <Modal show={deleteTUTModal} onHide={handleDeleteTUTModalClose}>
        <Modal.Header closeButton>
          <Modal.Title>Elimina Time Under Tension</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>
            Sei sicuro di voler cancellare il Time Under Tension{" "}
            <strong>
              {selectedTUT &&
                `${selectedTUT.ecc}.${selectedTUT.stopEcc}.${selectedTUT.conc}.${selectedTUT.stopConc}`}
            </strong>
            ?
          </p>
          <Alert variant="warning">
            <FontAwesomeIcon icon={faExclamationTriangle} />
            <span className="ms-2">Questa azione non può essere annullata.</span>
          </Alert>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="light" size="sm" className="border" onClick={handleDeleteTUTModalClose}>
            Chiudi
          </Button>
          <Button variant="danger" size="sm" onClick={handleDeleteTUT}>
            {loading ? <TailSpin width={20} height={20} /> : <span>Cancella</span>}
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
}
